import { Component } from '@theme/component';
import { OptionRenderer } from '@awh/options';

const euro = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

/** "3000x1000" → {w, h}. Null if it is not a pair. */
function parseSize(value) {
  const m = /^\s*([\d.]+)\s*[xX]\s*([\d.]+)\s*$/.exec(value ?? '');
  if (!m) return null;
  // Some entries are written "10.000" for ten metres — a thousands separator,
  // not a decimal point.
  const w = Number.parseInt(m[1].replace(/\./g, ''), 10);
  const h = Number.parseInt(m[2].replace(/\./g, ''), 10);
  return Number.isFinite(w) && Number.isFinite(h) ? { w, h } : null;
}

const inMeter = (mm) => `${(mm / 1000).toFixed(2).replace('.', ',')} m`;

/**
 * The configurator: size, colour, Optionize options, running total, add to
 * quote.
 *
 * Ported from ProductConfigurator.js and MaatKiezer.js. Used on the product
 * page and, through the Section Rendering fetch in awh-configurator.js, on
 * /pages/configurator — the same markup rendered by the same Liquid, because
 * "a second set of densities is exactly how two surfaces drift apart".
 *
 * Optionize is not reimplemented here. The app renders its own controls and
 * owns its own add-on pricing; this reads the option-set JSON the app prints
 * to know what the add-ons cost, and reads the app's inputs to know what was
 * chosen. Nothing here writes into the app's DOM.
 *
 * @extends {Component}
 */
class AwhConfigure extends Component {
  #variants = [];
  #matrix = null;
  #size = null;
  #colour = null;

  connectedCallback() {
    super.connectedCallback();
    this.#variants = this.#json('[data-variants]') ?? [];
    this.#buildMatrix();
    this.#renderSize();
    this.#bindColour();
    this.#mountOptions();
    this.addEventListener('change', this.#onChange);
    // Optionize hydrates asynchronously; recompute whenever anything inside it
    // changes rather than racing its boot.
    this.addEventListener('input', this.#onChange);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('input', this.#onChange);
  }

  #json(sel) {
    const el = this.querySelector(sel);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || 'null');
    } catch {
      return null;
    }
  }

  /* ---------------------------------------------------------------- sizes */

  #picker() {
    return this.querySelector('[data-size-picker]');
  }

  #buildMatrix() {
    const picker = this.#picker();
    if (!picker) return;
    const i = Number(picker.dataset.optionIndex) - 1;

    const rows = [];
    for (const v of this.#variants) {
      const pair = parseSize(v.options[i]);
      // One unparseable size and the whole product falls back to a plain
      // list, rather than showing a half-split control.
      if (!pair) return;
      rows.push({ ...pair, value: v.options[i], price: v.price });
    }
    if (!rows.length) return;

    const widths = [...new Set(rows.map((r) => r.w))].sort((a, b) => a - b);
    this.#matrix = {
      rows,
      widths,
      heightsAt: (w) => [...new Set(rows.filter((r) => r.w === w).map((r) => r.h))].sort((a, b) => a - b),
      priceAt: (w, h) => rows.find((r) => r.w === w && r.h === h)?.price ?? null,
      valueAt: (w, h) => rows.find((r) => r.w === w && r.h === h)?.value ?? null,
      fromAt: (w) => Math.min(...rows.filter((r) => r.w === w).map((r) => r.price)),
    };
    this.#size = rows[0].value;
  }

  #renderSize() {
    const picker = this.#picker();
    if (!picker || !this.#matrix) return;
    const d = picker.dataset;
    const cur = parseSize(this.#size);
    if (!cur) return;

    const heights = this.#matrix.heightsAt(cur.w);
    const cheapest = Math.min(...heights.map((h) => this.#matrix.priceAt(cur.w, h)).filter((p) => p != null));

    const chip = (name, val, on, label, note) => `
      <label class="maatchip${on ? ' is-selected' : ''}">
        <input type="radio" name="${name}" class="visually-hidden" value="${val}"${on ? ' checked' : ''}>
        <span class="maatchip__maat">${label}</span>
        ${note ? `<span class="maatchip__prijs">${note}</span>` : ''}
      </label>`;

    picker.innerHTML = `
      <fieldset class="maatkiezer__groep">
        <legend class="maatkiezer__label">${d.labelWidth}</legend>
        <div class="maatkiezer__chips" data-widths>
          ${this.#matrix.widths
            .map((w) => chip('awh-w', w, w === cur.w, inMeter(w), `vanaf ${euro.format(this.#matrix.fromAt(w) / 100)}`))
            .join('')}
        </div>
      </fieldset>
      <fieldset class="maatkiezer__groep">
        <legend class="maatkiezer__label">${d.labelHeight}</legend>
        <div class="maatkiezer__chips" data-heights>
          ${heights
            .map((h) => {
              const p = this.#matrix.priceAt(cur.w, h);
              const more = p != null ? p - cheapest : 0;
              return chip('awh-h', h, h === cur.h, inMeter(h), more > 0 ? `+ ${euro.format(more / 100)}` : d.labelIncluded);
            })
            .join('')}
        </div>
      </fieldset>`;
  }

  #bindColour() {
    const cp = this.querySelector('[data-colour-picker]');
    this.#colour = cp?.querySelector('input:checked')?.value ?? null;
    // A product whose first variant is already the custom colour arrives with
    // the line needed, so it is settled here as well as on change.
    this.#andereKleur();
  }

  /* --------------------------------------------------------------- events */

  #onChange = (event) => {
    const t = event.target;
    if (!(t instanceof HTMLElement)) return;

    if (t.matches('[data-widths] input')) {
      const cur = parseSize(this.#size);
      const w = Number(/** @type {HTMLInputElement} */ (t).value);
      const avail = this.#matrix.heightsAt(w);
      // Keep the height if this width is made in it; otherwise fall to the
      // nearest that exists, rather than silently jumping to the smallest.
      const h = avail.includes(cur.h)
        ? cur.h
        : avail.reduce((a, b) => (Math.abs(b - cur.h) < Math.abs(a - cur.h) ? b : a));
      this.#size = this.#matrix.valueAt(w, h);
      this.#renderSize();
    } else if (t.matches('[data-heights] input')) {
      const cur = parseSize(this.#size);
      this.#size = this.#matrix.valueAt(cur.w, Number(/** @type {HTMLInputElement} */ (t).value));
      this.#renderSize();
    } else if (t.matches('[data-colour-picker] input')) {
      this.#colour = /** @type {HTMLInputElement} */ (t).value;
      // `.qbkleur__chip`, not `.maatchip` — the colour chips are a different
      // component from the size chips. Against `.maatchip` this loop matched
      // nothing, so the radio changed and the ring never moved: the picker
      // looked dead while the variant underneath it was in fact changing.
      for (const l of this.querySelectorAll('[data-colour-picker] .qbkleur__chip')) {
        l.classList.toggle('is-selected', l.contains(t));
      }
      this.#andereKleur();
    }
    this.#update();
  };

  /**
   * The surcharge line belongs to one choice, so it is shown with that choice
   * and hidden with every other.
   *
   * "aanvraag" is the same test snippets/awh-colour.liquid uses to decide a
   * chip is the custom one. The catalogue writes that option sixteen ways —
   * "Op aanvraag", "kleur-op-aanvraag" — and the substring is what survives
   * all of them; matching the full value would break on the next spelling.
   */
  #andereKleur() {
    const note = this.querySelector('[data-anders]');
    if (!note) return;
    note.hidden = !String(this.#colour ?? '')
      .toLowerCase()
      .includes('aanvraag');
  }

  /* ---------------------------------------------------------------- price */

  /** The option set the app prints for this product. */
  #optionSet() {
    const el = document.querySelector('[id^="optionize-option-set-"]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || 'null');
    } catch {
      return null;
    }
  }

  /**
   * Render the options ourselves.
   *
   * The app supplies the data; the controls are the prototype's. Reading the
   * app's rendered DOM was the wrong way round — its markup is not a contract
   * and could not be styled to match. Its own UI is hidden by CSS, so it keeps
   * whatever bookkeeping it does and we simply do not show it.
   */
  #mountOptions() {
    const set = this.#optionSet();
    const mount = this.querySelector('[data-options]');
    if (!set || !mount) return;

    this.options = new OptionRenderer(mount, set, () => this.#update(), 2);
  }

  #variant() {
    return this.#variants.find(
      (v) => (!this.#size || v.options.includes(this.#size)) && (!this.#colour || v.options.includes(this.#colour))
    );
  }

  #update() {
    const el = this.querySelector('[data-total]');
    const v = this.#variant();
    const base = v ? v.price / 100 : 0;
    const add = this.options ? this.options.price.total : 0;
    if (el) el.textContent = euro.format(base + add);

    // What is still missing goes above the bar, not in it — the bar stays a
    // label, a price and an action.
    const missing = this.options ? this.options.problems : [];
    const note = this.querySelector('[data-missing]');
    if (note) {
      note.hidden = missing.length === 0;
      note.innerHTML = missing.length
        ? 'Nog in te vullen: ' +
          missing
            .map(
              (f) =>
                `<a href="#optie-${f.optionId}">${f.label}${f.reason === 'minimum' ? ` (minimaal ${f.minimum})` : ''}</a>`
            )
            .join(', ')
        : '';
    }
    const btn = this.querySelector('[data-add]');
    if (btn) btn.toggleAttribute('disabled', missing.length > 0);
  }

  /* ------------------------------------------------------------------ add */

  /** `event.target`, not `currentTarget` — component.js delegates on document. */
  add = async () => {
    const btn = this.querySelector('[data-add]');
    const v = this.#variant();
    if (!v) return;

    if (this.options && this.options.problems.length) {
      this.options.showProblems(this.options.problems);
      this.querySelector('[data-missing] a')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const properties = {};
    if (this.#size) properties['Maat'] = this.#size;
    if (this.#colour) properties['Kleur'] = this.#colour;

    // Priced options travel as one property, not as extra line items.
    //
    // Shopify prices a line from its variant, so an option written as a plain
    // property costs nothing — a poort with a € 1.695 motor went into the cart
    // at the bare variant price. The obvious fix, adding Optionize's generated
    // add-on variants as sibling lines, is refused by the app's own cart
    // validation:
    //
    //   422 "Ja, met poortmotor" can only be purchased as an add-on with a
    //       main product.
    //
    // Those variants are only additable through Optionize's own flow, which we
    // replaced. So the priced choices are carried in `_awh_opties` and the
    // quote panel prices them: the panel's total is right, and the payload sent
    // to the quote proxy is expanded into real add-on lines before it goes.
    // The Shopify cart's own subtotal stays variant-only — nothing reads it,
    // because this store has no checkout.
    const priced = (this.options ? this.options.price.lines : []).filter((l) => l.price > 0);
    const taken = new Set(priced.map((l) => `${l.option}\u0000${l.value}`));

    if (priced.length) {
      properties['_awh_opties'] = JSON.stringify(
        priced.map((l) => ({ n: l.option, k: l.key, v: l.value, p: Math.round(l.price * 100) }))
      );
    }

    // Everything that costs nothing stays a readable property, so the quote
    // shows what was chosen and not only what was charged for.
    for (const a of this.options ? this.options.summary : []) {
      if (taken.has(`${a.option}\u0000${a.value}`)) continue;
      properties[a.option] = a.value;
    }
    for (const n of this.options ? this.options.notes : []) properties[n.option] = n.value;

    // The two properties above are written under the question the visitor read,
    // and Optionize files its own default under the option's internal name —
    // "Hekwerk laten plaatsen?" against "Montage". Both survive onto the line,
    // so a quote showed the answer and the default side by side, saying Ja and
    // Nee to one question. A question hidden by conditional logic was worse: no
    // answer to pair with the default, so the default read as the answer.
    //
    // `_awh_vrij` carries the same free choices keyed the way Optionize keys
    // them, and `_awh_keys` names every option it owns on this product. At
    // submission awh-quote.js clears all of those keys and writes the answers
    // back, which leaves one line per question and nothing for a hidden one.
    //
    // Declines ride along here although the readable summary drops them: "Nee"
    // is a real answer, and without it there is nothing to overwrite the
    // identical-looking default with.
    const free = [];
    for (const a of this.options ? this.options.answers : []) {
      if (taken.has(`${a.option}\u0000${a.value}`)) continue;
      free.push({ n: a.option, k: a.key ?? a.option, v: a.value });
    }
    for (const n of this.options ? this.options.notes : []) {
      free.push({ n: n.option, k: n.key ?? n.option, v: n.value });
    }
    if (free.length) properties['_awh_vrij'] = JSON.stringify(free);

    const keys = this.options ? this.options.keys : [];
    if (keys.length) properties['_awh_keys'] = JSON.stringify(keys);

    const code = this.dataset.modelCode;
    if (code) properties['_model'] = code;

    btn?.setAttribute('disabled', '');
    // The add is a round trip to /cart/add.js and the quote panel opens only
    // once it lands. Without this the button just greys out and the page sits
    // still for a second, which reads as a dead click.
    btn?.classList.add('is-busy');
    try {
      const res = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: v.id, quantity: 1, properties }] }),
      });
      if (!res.ok) throw new Error(String(res.status));

      // Our own panel, not Horizon's cart drawer. This site has no checkout,
      // so the drawer was both the wrong content and the wrong promise; the
      // quote panel shows what was just configured and asks for the request.
      document.dispatchEvent(new CustomEvent('awh:quote:open'));
    } finally {
      btn?.removeAttribute('disabled');
      btn?.classList.remove('is-busy');
    }
  };
}

if (!customElements.get('awh-configure-component')) {
  customElements.define('awh-configure-component', AwhConfigure);
}
